import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { FileText, Loader2, Trash2, Upload, X } from 'lucide-react';
import {
  getIntakeDraft,
  removeIntakeDraft,
  startReferralFromDraft,
  type IntakeDraft,
  type IntakeDraftDocument,
} from '../../services/intakeDrafts';
import { PermissionSlug } from '../../constants/featureFlags';
import { useStore } from '../../store';

/**
 * A draft referral, opened as the upload dialog it is about to become.
 *
 * Clicking a draft does not open an intake, because there is no intake: it
 * opens the same Upload Documents step a referral has always started from,
 * with the faxes already gathered standing in the list. That is what unlocks
 * adding the documents that did not arrive by fax, which until now could only
 * be done by uploading the whole packet by hand.
 */

export interface DraftIntakeDialogProps {
  draft: IntakeDraft | null;
  onClose: () => void;
  /** A referral exists now. The caller decides where to send the person. */
  onStarted: (intakeId: string) => void;
  onRemoved: () => void;
  onError: (message: string) => void;
}

function pages(document: IntakeDraftDocument): string {
  if (document.page_count === null) return '';
  return document.page_count === 1 ? '1 page' : `${document.page_count} pages`;
}

export default function DraftIntakeDialog({
  draft,
  onClose,
  onStarted,
  onRemoved,
  onError,
}: DraftIntakeDialogProps) {
  const [gathered, setGathered] = useState<IntakeDraftDocument[]>([]);
  const [added, setAdded] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const browse = useRef<HTMLInputElement | null>(null);
  // Starting a referral reads the faxes, which the server refuses without eFax
  // or without the right to manage the fax inbox.
  const canStart = useStore(
    state =>
      state.hasProduct('fax') &&
      state.hasPermission(PermissionSlug.FAX_INBOX_MANAGE)
  );

  useEffect(() => {
    setAdded([]);
    setGathered([]);
    if (!draft) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const detail = await getIntakeDraft(draft.id);
        if (!cancelled) setGathered(detail.documents);
      } catch {
        if (!cancelled) onError('Could not load that draft.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [draft, onError]);

  const take = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      const pdfs = Array.from(files).filter(
        file => file.type === 'application/pdf'
      );
      if (pdfs.length !== files.length) {
        onError('Only PDF files can be added to a referral.');
      }
      if (pdfs.length > 0) setAdded(previous => [...previous, ...pdfs]);
    },
    [onError]
  );

  const start = useCallback(async () => {
    if (!draft) return;
    setStarting(true);
    try {
      const started = await startReferralFromDraft(draft.id, added);
      onStarted(started.intake_id);
    } catch {
      onError('Could not start a referral from that draft.');
    } finally {
      setStarting(false);
    }
  }, [draft, added, onStarted, onError]);

  const remove = useCallback(async () => {
    if (!draft) return;
    setRemoving(true);
    try {
      await removeIntakeDraft(draft.id);
      onRemoved();
    } catch {
      onError('Could not discard that draft.');
    } finally {
      setRemoving(false);
    }
  }, [draft, onRemoved, onError]);

  const total = gathered.length + added.length;
  const busy = starting || removing;

  return (
    <Dialog open={draft !== null} onOpenChange={open => !open && onClose()}>
      <DialogContent className='flex max-h-[90vh] max-w-xl flex-col'>
        <DialogHeader>
          <DialogTitle>Draft intake: {draft?.name}</DialogTitle>
        </DialogHeader>

        <div className='min-h-0 flex-1 space-y-4 overflow-y-auto'>
          <Card
            className={`border-2 border-dashed p-8 text-center transition-colors ${
              dragging
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-muted-foreground/50'
            }`}
            onDragOver={event => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={event => {
              event.preventDefault();
              setDragging(false);
            }}
            onDrop={event => {
              event.preventDefault();
              setDragging(false);
              take(event.dataTransfer.files);
            }}
          >
            <Upload className='mx-auto mb-4 h-12 w-12 text-muted-foreground' />
            <h3 className='mb-2 text-lg font-semibold'>
              Add documents that did not come by fax
            </h3>
            <p className='mb-4 text-muted-foreground'>
              Drag and drop PDFs here, or click to browse
            </p>
            <input
              ref={browse}
              type='file'
              accept='application/pdf'
              multiple
              className='hidden'
              aria-label='Add PDF documents'
              onChange={event => {
                take(event.target.files);
                // Cleared so choosing the same file twice still fires change.
                event.target.value = '';
              }}
            />
            <Button
              variant='outline'
              type='button'
              disabled={busy}
              onClick={() => browse.current?.click()}
            >
              Browse Files
            </Button>
          </Card>

          {loading ? (
            <div className='flex justify-center py-6'>
              <Loader2 className='h-5 w-5 animate-spin' />
            </div>
          ) : (
            <ul className='space-y-1.5'>
              {gathered.map(document => (
                <li
                  key={`${document.kind}-${document.id}`}
                  className='flex items-center gap-2 rounded border bg-white px-3 py-2 text-sm'
                >
                  <FileText className='h-4 w-4 shrink-0 text-muted-foreground' />
                  <span className='min-w-0 flex-1 truncate'>
                    {document.name}
                  </span>
                  <span className='whitespace-nowrap text-xs text-muted-foreground'>
                    {pages(document)}
                    {pages(document) && ' · '}from the fax inbox
                  </span>
                </li>
              ))}
              {added.map((file, index) => (
                <li
                  key={`${file.name}-${index}`}
                  className='flex items-center gap-2 rounded border bg-white px-3 py-2 text-sm'
                >
                  <FileText className='h-4 w-4 shrink-0 text-muted-foreground' />
                  <span className='min-w-0 flex-1 truncate'>{file.name}</span>
                  <span className='whitespace-nowrap text-xs text-muted-foreground'>
                    added here
                  </span>
                  <Button
                    variant='ghost'
                    size='sm'
                    aria-label={`Remove ${file.name}`}
                    disabled={busy}
                    onClick={() =>
                      setAdded(previous =>
                        previous.filter((_, at) => at !== index)
                      )
                    }
                  >
                    <X className='h-4 w-4' />
                  </Button>
                </li>
              ))}
              {total === 0 && (
                <li className='rounded border border-dashed px-3 py-4 text-center text-sm text-muted-foreground'>
                  Nothing gathered yet. Assign a fax to this draft from the fax
                  inbox, or add a document above.
                </li>
              )}
            </ul>
          )}

          <div className='rounded-lg border border-blue-200 bg-blue-50 p-4'>
            <h4 className='mb-2 text-sm font-medium text-blue-900'>
              Background Processing
            </h4>
            <p className='text-sm text-blue-700'>
              After you start the referral, the documents are automatically
              processed for text extraction and AI analysis in the background.
              You can view the processing status in the intake history.
            </p>
          </div>
        </div>

        <div className='flex shrink-0 items-center justify-between gap-2 pt-2'>
          <Button
            variant='ghost'
            type='button'
            disabled={busy}
            onClick={() => void remove()}
          >
            {removing ? (
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            ) : (
              <Trash2 className='mr-2 h-4 w-4' />
            )}
            Discard draft
          </Button>
          <div className='flex gap-2'>
            <Button variant='outline' onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            {/* Nothing to extract from an empty draft, so the referral it made
                would sit in the Intake Manager with no pages and no way to
                fill it in. */}
            {canStart && (
              <Button
                disabled={busy || total === 0}
                onClick={() => void start()}
              >
                {starting && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                Start referral from {total}{' '}
                {total === 1 ? 'document' : 'documents'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
